/**
 * Onboarding-health dashboard — Zod schemas.
 *
 * Wire shape mirrors the Go `Status` type in
 * internal/tenant/onboardinghealth/onboardinghealth.go. Empty
 * arrays must serialise as `[]` not `null` — a server-side
 * regression that returns `null` will surface as a parse error
 * here rather than a runtime crash inside the page.
 */

import { z } from "zod";

export const eventCountsSchema = z.object({
  authentication_success: z.number().int().nonnegative(),
  authentication_failed: z.number().int().nonnegative(),
  registration_success: z.number().int().nonnegative(),
  registration_failed: z.number().int().nonnegative(),
  enrollment_completed: z.number().int().nonnegative(),
  enrollment_expired: z.number().int().nonnegative(),
});
export type EventCounts = z.infer<typeof eventCountsSchema>;

// P1-5: the backend now SUBSTRINGs to 64 chars at the SQL layer
// (onboardinghealth.go) so this max never trips on legitimate
// audit metadata; the cap stays for defense in depth.
export const failureReasonSchema = z.object({
  reason: z.string().min(1).max(64),
  count: z.number().int().nonnegative(),
});
export type FailureReason = z.infer<typeof failureReasonSchema>;

export const otpDeliverySchema = z.object({
  sent: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  // Undelivered work. Without it a stalled queue and an idle tenant render identically.
  queued: z.number().int().nonnegative(),
});
export type OTPDelivery = z.infer<typeof otpDeliverySchema>;

// D-6 — per-environment-class onboarding success breakdown.
// The `env_class` string is one of the canonical
// infer-environment.ts classes (modern_capable, fragile_vm,
// vdi_class, etc.) OR "unknown" when the audit row lacked the
// metadata key. P1-5: backend SUBSTRINGs to 64 chars; the
// frontend cap mirrors that.
export const envClassRowSchema = z.object({
  env_class: z.string().min(1).max(64),
  succeeded: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
});
export type EnvClassRow = z.infer<typeof envClassRowSchema>;

export const onboardingHealthStatusSchema = z.object({
  window_hours: z.number().int().positive(),
  events: eventCountsSchema,
  top_failure_reasons: z.array(failureReasonSchema),
  otp_delivery: otpDeliverySchema,
  env_class_breakdown: z.array(envClassRowSchema).default([]),
});
export type OnboardingHealthStatus = z.infer<typeof onboardingHealthStatusSchema>;
